import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import api from "@/utils/api";
import {
  ArrowLeft, ChevronLeft, ChevronRight, Wrench, AlertTriangle, CheckCircle2
} from "lucide-react";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const PmCalendar = () => {
  const navigate = useNavigate();
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1); // 1-12
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // date string

  useEffect(() => {
    setLoading(true);
    api.get(`/pm-calendar?year=${year}&month=${month}`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [year, month]);

  const daysInMonth = new Date(year, month, 0).getDate();
  const firstWeekday = new Date(year, month - 1, 1).getDay();
  const cells = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const eventsByDate = {};
  (data?.past_events || []).forEach((e) => {
    (eventsByDate[e.event_date] = eventsByDate[e.event_date] || []).push({
      ...e, kind: "past",
    });
  });
  (data?.projections || []).forEach((p) => {
    (eventsByDate[p.projected_date] = eventsByDate[p.projected_date] || []).push({
      ...p, kind: "projected",
    });
  });

  const nav = (delta) => {
    const d = new Date(year, month - 1 + delta, 1);
    setYear(d.getFullYear());
    setMonth(d.getMonth() + 1);
  };

  const todayIso = today.toISOString().slice(0, 10);
  const selectedEvents = (selected && eventsByDate[selected]) || [];

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <div className="max-w-7xl mx-auto p-6">
        <button onClick={() => navigate(-1)} className="text-sm text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 mb-4">
          <ArrowLeft size={14} /> Back
        </button>

        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div>
            <h1 className="text-2xl font-extrabold text-slate-900 inline-flex items-center gap-2">
              <Wrench size={22} className="text-[#1A3560]" /> PM Calendar
            </h1>
            <p className="text-sm text-slate-500">All logged &amp; projected PM events. Colors are per tool.</p>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="cal-prev" onClick={() => nav(-1)} className="p-2 rounded-lg bg-white border border-slate-300 hover:bg-slate-100">
              <ChevronLeft size={16} />
            </button>
            <div className="text-lg font-extrabold text-slate-900 min-w-[180px] text-center">
              {MONTHS[month - 1]} {year}
            </div>
            <button data-testid="cal-next" onClick={() => nav(1)} className="p-2 rounded-lg bg-white border border-slate-300 hover:bg-slate-100">
              <ChevronRight size={16} />
            </button>
            <button
              onClick={() => { setYear(today.getFullYear()); setMonth(today.getMonth() + 1); }}
              className="ml-1 text-xs font-bold text-[#1A3560] px-3 py-1.5 rounded-lg border border-[#1A3560] hover:bg-[#1A3560]/10"
            >
              Today
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center text-slate-400 py-10">Loading...</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Calendar grid */}
            <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-slate-200 p-4">
              <div className="grid grid-cols-7 gap-1 mb-1">
                {DAY_LABELS.map((d) => (
                  <div key={d} className="text-center text-[10px] font-extrabold uppercase tracking-widest text-slate-400 py-1">
                    {d}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {cells.map((day, i) => {
                  if (!day) return <div key={`empty-${i}`} className="h-24" />;
                  const iso = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                  const evs = eventsByDate[iso] || [];
                  const isToday = iso === todayIso;
                  const isSel = iso === selected;
                  return (
                    <button
                      key={iso}
                      data-testid={`cal-day-${iso}`}
                      onClick={() => setSelected(iso)}
                      className={`text-left h-24 rounded-lg border p-1.5 relative overflow-hidden transition-all ${
                        isSel ? "border-[#1A3560] ring-2 ring-[#1A3560]" :
                        isToday ? "border-emerald-400 bg-emerald-50" :
                        evs.length ? "border-slate-300 hover:bg-slate-50" :
                        "border-slate-100 hover:bg-slate-50"
                      }`}
                    >
                      <div className={`text-xs font-extrabold ${isToday ? "text-emerald-700" : "text-slate-700"}`}>{day}</div>
                      <div className="mt-1 space-y-0.5">
                        {evs.slice(0, 3).map((e, j) => {
                          const color = data?.color_by_tool?.[e.tool_no] || "#94A3B8";
                          const isProj = e.kind === "projected";
                          const isOverdue = e.status === "OVERDUE";
                          return (
                            <div
                              key={`${e.tool_no}-${e.kind}-${j}`}
                              className={`text-[9px] font-bold truncate px-1 py-0.5 rounded ${isProj ? "" : "text-white"}`}
                              style={{
                                background: isProj ? "transparent" : color,
                                border: isProj ? `1.5px dashed ${color}` : "none",
                                color: isProj ? color : "white",
                              }}
                              title={`T${e.tool_no} · ${e.kind} · ${e.part_name || ""}`}
                            >
                              {isOverdue && "⚠ "}T{e.tool_no}
                            </div>
                          );
                        })}
                        {evs.length > 3 && (
                          <div className="text-[9px] font-bold text-slate-500 pl-1">+{evs.length - 3} more</div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Legend */}
              <div className="mt-3 flex items-center gap-3 text-[10px] text-slate-500 border-t border-slate-100 pt-2">
                <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-slate-400" /> Past PM</span>
                <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded border-2 border-dashed border-slate-400" /> Projected</span>
                <span className="inline-flex items-center gap-1">⚠ Overdue</span>
              </div>
            </div>

            {/* Right column: selected-day details + tool legend */}
            <div className="space-y-4">
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4">
                <p className="text-xs font-extrabold uppercase tracking-widest text-slate-500 mb-2">
                  {selected ? selected : "Select a date"}
                </p>
                {!selected ? (
                  <p className="text-slate-400 text-sm">Tap a day to see PM events / projections.</p>
                ) : selectedEvents.length === 0 ? (
                  <p className="text-slate-400 text-sm inline-flex items-center gap-1">
                    <CheckCircle2 size={14} className="text-emerald-500" /> Nothing scheduled
                  </p>
                ) : (
                  <ul className="space-y-2" data-testid="cal-selected-events">
                    {selectedEvents.map((e, i) => {
                      const color = data?.color_by_tool?.[e.tool_no] || "#94A3B8";
                      return (
                        <li key={`${e.tool_no}-${e.kind}-${i}`} className="border border-slate-200 rounded-lg p-2">
                          <div className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded" style={{ background: color }} />
                            <button
                              onClick={() => navigate(`/admin/tool-history/${e.tool_no}`)}
                              className="font-extrabold text-slate-900 text-sm hover:underline"
                            >
                              T{e.tool_no}
                            </button>
                            <span className={`text-[10px] font-extrabold px-1.5 py-0.5 rounded-full ${
                              e.status === "OVERDUE" ? "bg-red-100 text-red-700" :
                              e.kind === "projected" ? "bg-amber-100 text-amber-700" :
                              "bg-slate-100 text-slate-600"
                            }`}>
                              {e.status || (e.kind === "projected" ? "PROJECTED" : (e.event_type || "PM"))}
                            </span>
                          </div>
                          {e.part_name && <p className="text-xs text-slate-500 mt-1">{e.part_name}</p>}
                          {e.kind === "past" && e.action_taken && (
                            <p className="text-xs text-slate-600 mt-1"><b>Action:</b> {e.action_taken}</p>
                          )}
                          {e.kind === "projected" && (
                            <p className="text-xs text-slate-600 mt-1">
                              in <b>{e.days_ahead}</b> days · {e.daily_rate || 0} strokes/day ·
                              PM at {e.next_pm_at?.toLocaleString?.() || e.next_pm_at}
                            </p>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4">
                <p className="text-xs font-extrabold uppercase tracking-widest text-slate-500 mb-2">
                  Tools in this month
                </p>
                <ul className="space-y-1 max-h-64 overflow-y-auto">
                  {Object.entries(data?.color_by_tool || {}).map(([tn, color]) => (
                    <li key={tn} className="flex items-center gap-2 text-xs">
                      <span className="w-3 h-3 rounded" style={{ background: color }} />
                      <button
                        onClick={() => navigate(`/admin/tool-history/${tn}`)}
                        className="text-slate-700 hover:underline font-bold"
                      >
                        T{tn}
                      </button>
                    </li>
                  ))}
                  {Object.keys(data?.color_by_tool || {}).length === 0 && (
                    <p className="text-slate-400 text-sm inline-flex items-center gap-1">
                      <AlertTriangle size={12} /> No tools with PM events this month
                    </p>
                  )}
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PmCalendar;
